import type { InvestigationReportData } from '../types';

/**
 * Sample OpenSRE-shaped report used by demo mode and UI development.
 * Replace with live writeup output when SRE Agent is wired.
 */
export const SAMPLE_INVESTIGATION_REPORT: InvestigationReportData = {
  version: 1,
  title: 'Elevated CPU on production application VM',
  severity: 'high',
  status: 'ongoing',
  affected_services: ['app-vm-01', 'api-gateway'],
  executive_summary:
    'Azure Monitor fired a high CPU alert on app-vm-01. Log Analytics and metrics show a sustained CPU spike correlated with a noisy worker process. Root cause is probable process contention; immediate mitigation is to restart the worker and scale if needed.',
  impact: {
    user_facing: 'Intermittent API latency and elevated error rates for checkout flows.',
    service_impact: 'app-vm-01 CPU above 90% for 25+ minutes; dependent API gateway queueing.',
    blast_radius: 'Production engagement subscription; single VM and upstream gateway.',
  },
  timeline: [
    {
      time: '2026-08-24T08:32:00Z',
      event: 'Azure Monitor alert fired: Percentage CPU > 90%',
    },
    {
      time: '2026-08-24T08:33:00Z',
      event: 'Alert ingested into Pulse and correlated to engagement resources',
    },
    {
      time: '2026-08-24T08:34:00Z',
      event: 'Investigation started — Log Analytics query for Perf and Event tables',
    },
    {
      time: '2026-08-24T08:35:00Z',
      event: 'Metrics confirm sustained CPU; top process identified as worker.exe',
    },
    {
      time: '2026-08-24T08:36:00Z',
      event: 'Resource Graph and VM describe completed; no recent redeploy detected',
    },
    {
      time: '2026-08-24T08:37:00Z',
      event: 'Hypotheses ranked; report synthesized',
    },
  ],
  root_cause: {
    summary:
      'Sustained CPU saturation on app-vm-01 driven by an unconstrained background worker process.',
    confidence: 'probable',
    details:
      'Perf counters and process samples align with worker.exe consuming majority CPU. No evidence yet of host-level Azure platform fault. Confirm with guest metrics or Run Command in a follow-up.',
  },
  action_items: [
    {
      priority: 'immediate',
      action: 'Restart the worker service on app-vm-01 and verify CPU returns below 60%.',
    },
    {
      priority: 'short_term',
      action: 'Add process-level CPU alert and review worker concurrency settings.',
    },
    {
      priority: 'long_term',
      action: 'Move batch work to a scalable worker pool and add autoscaling policy.',
    },
  ],
  lessons_learned: [
    'Start with Log Analytics aggregations before raw log sampling.',
    'Correlate Pulse alert metadata (resourceId) early to scope Azure queries.',
    'Promote this alert signature to a deterministic playbook once confirmed stable.',
  ],
};

export const SAMPLE_CONCLUSION_MARKDOWN = `## SRE Investigation Report

**Title:** Elevated CPU on production application VM  
**Severity:** high | **Status:** ongoing

### Executive Summary
Azure Monitor fired a high CPU alert on app-vm-01. Metrics and Log Analytics point to a noisy worker process causing sustained CPU saturation.

### Root Cause (probable)
Sustained CPU saturation on app-vm-01 driven by an unconstrained background worker process.

### Recommended Actions
1. **Immediate:** Restart the worker service and verify CPU recovery.
2. **Short-term:** Add process-level alerting and review concurrency.
3. **Long-term:** Move batch work to a scalable worker pool.
`;
